/**
 * AdminToolsDrawer — quick-access tools sheet for the admin left sidebar.
 *
 * Trigger: a full-width sidebar button pinned below the scrollable tab list.
 * Indicator: amber count badge (3 tools) + pulsing dot so admins know the
 * drawer is populated before they open it.
 *
 * Content:
 *   · Bookstore Admin shortcut
 *   · Tier Impersonation Card (view the app as any subscription tier)
 *   · LLM Cost & Balance Card
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { SlidersHorizontal, Store } from 'lucide-react';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet';
import TierImpersonationCard from './TierImpersonationCard';
import LlmCostCard from './LlmCostCard';

const TOOL_COUNT = 3; // Bookstore Admin + Tier Impersonation + LLM Cost

export const AdminToolsDrawer = () => (
  <Sheet>
    {/* ── Sidebar trigger — full-width, pinned below the scrollable list ── */}
    <SheetTrigger asChild>
      <button
        type="button"
        data-testid="admin-tools-drawer-open"
        className={[
          'group flex h-9 w-full items-center gap-2 rounded-sm border px-2.5 text-[12px] font-medium',
          'border-amber-200/70 bg-amber-50/50 text-amber-800',
          'transition-colors hover:border-amber-300 hover:bg-amber-100/70',
          'dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-400',
          'dark:hover:border-amber-700/60 dark:hover:bg-amber-900/40',
        ].join(' ')}
      >
        {/* Icon */}
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-500" />

        {/* Label */}
        <span className="flex-1 text-left">Quick Tools</span>

        {/* Feature count badge */}
        <span
          className="inline-flex items-center justify-center rounded-full bg-amber-600/15 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
          aria-label={`${TOOL_COUNT} tools available`}
        >
          {TOOL_COUNT}
        </span>

        {/* Pulsing dot — signals "there's something in here" at a glance */}
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60 dark:bg-amber-500" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500 dark:bg-amber-400" />
        </span>
      </button>
    </SheetTrigger>

    {/* ── Drawer content ─────────────────────────────────────────────────── */}
    <SheetContent
      side="left"
      className="w-[min(92vw,430px)] overflow-y-auto sm:max-w-[430px]"
      data-testid="admin-tools-drawer"
    >
      <SheetHeader className="pr-7">
        <SheetTitle>Quick Tools</SheetTitle>
        <SheetDescription>
          Cross-surface shortcuts, tier testing, and model-cost controls.
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-4">
        {/* ── Bookstore Admin shortcut ─────────────────────────────── */}
        <Link
          to="/bookstore/admin"
          data-testid="admin-tools-bookstore-admin"
          className="flex items-center gap-3 rounded-md border border-border bg-card p-3 transition-colors hover:border-primary no-underline"
        >
          <Store className="h-4 w-4 text-primary shrink-0" />
          <div>
            <div className="text-[13px] font-semibold text-foreground">Bookstore Admin</div>
            <div className="text-[11px] text-muted-foreground">
              Catalog, shelves, promotions, and readers
            </div>
          </div>
        </Link>

        {/* ── Tier impersonation ───────────────────────────────────── */}
        <section className="space-y-2" data-testid="admin-tools-tier-section">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            View as a tier
          </div>
          <TierImpersonationCard />
        </section>

        {/* ── LLM cost & balance ───────────────────────────────────── */}
        <section className="space-y-2" data-testid="admin-tools-llm-section">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            LLM cost &amp; balance
          </div>
          <LlmCostCard />
        </section>
      </div>
    </SheetContent>
  </Sheet>
);
